import {Button} from 'platform/components';
import {Box, Clickable, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {type SellerOfferAlternatives} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {type OfferAlternative} from '../types/OfferAlternative';
import {getAlternativeLabel} from '../utils/getAlternativeLabel';

interface OfferAlternativesStripProps {
  offerAlternatives: SellerOfferAlternatives;
  alternatives: OfferAlternative[];
  activeAlternativeId: string;
  /** Same gate as the deal vehicle's total price. */
  canReadTotalPrice: boolean;
  onSelect: (alternativeId: string) => void;
  onAdd: VoidFunction;
}

/** Option cards above the vehicle: one per alternative, plus "Add alternative". */
export function OfferAlternativesStrip(props: OfferAlternativesStripProps) {
  const formatCurrency = useFormatCurrency();
  const {customerChoice} = props.offerAlternatives;

  return (
    <HStack spacing={2} align="stretch" wrap>
      {props.alternatives.map((alternative) => {
        const isActive = alternative.id === props.activeAlternativeId;
        const isChosen =
          customerChoice?.alternativeId === alternative.id && customerChoice.isCurrent;

        return (
          <Clickable
            key={alternative.id}
            role="button"
            tabIndex={0}
            aria-pressed={isActive}
            onClick={() => props.onSelect(alternative.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                props.onSelect(alternative.id);
              }
            }}
            data-testid={`offerAlternativeTab-${alternative.letter}`}
          >
            <Box
              height="100%"
              minWidth={36}
              paddingHorizontal={3}
              paddingVertical={2}
              borderRadius="small"
              border="1px solid"
              borderColor={isActive ? 'palettes.blue.60.100' : 'palettes.neutral.40.100'}
              backgroundColor={isActive ? 'palettes.blue.10.100' : 'palettes.white.10.100'}
              boxShadow={isActive ? 'elevation_1' : undefined}
              transition="border-color 150ms ease-out, background-color 150ms ease-out"
            >
              <VStack spacing={1}>
                <Text size="small" alternative color={isActive ? 'link' : 'primary'}>
                  {getAlternativeLabel(alternative)}
                </Text>
                <HStack spacing={1} align="center">
                  <Show when={props.canReadTotalPrice}>
                    <Text size="xSmall" color="secondary">
                      {isNil(alternative.terms.total)
                        ? EMPTY_PLACEHOLDER
                        : formatCurrency(
                            Number(alternative.terms.total.withVat.amount),
                            alternative.terms.total.withVat.currency
                          )}
                    </Text>
                  </Show>
                  <Show when={isChosen}>
                    <Icon value="action/check_circle" size={4} color="severity.success" />
                  </Show>
                </HStack>
              </VStack>
            </Box>
          </Clickable>
        );
      })}
      <Show when={props.offerAlternatives.canAdd}>
        <HStack align="center">
          <Button
            variant="link"
            leftIcon="content/add_circle"
            title={i18n.t('entity.offerAlternative.actions.add', {defaultValue: 'Add alternative'})}
            onClick={props.onAdd}
            data-testid="offerAlternativeAdd"
          />
        </HStack>
      </Show>
    </HStack>
  );
}
